import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Badge,
    Button,
    Col,
    Container,
    Input,
    ListGroup,
    ListGroupItem,
    Row
} from 'reactstrap';
import {
    FaFileAlt,
    FaLink,
    FaPaperPlane,
    FaQuestionCircle
} from 'react-icons/fa';
import {
    CitationInfo,
    CitationType,
    ContentPartDto,
    ItemDto,
    MessageItemDto,
    OutputTextContentPartDto,
    ResponsesMessageRecord,
    ResponsesMessageType,
    StreamingUpdateData
} from './responses-types';
import MessageBubble from './MessageBubble';
import { buildTurnFileIdMap } from './replaceCitationPlaceholders';
import { useNavigate } from 'react-router-dom';
import { ThreadContext } from './ThreadContext.tsx';
import { useMutation } from 'react-query';
import usePostCreateThread from '../../api/usePostCreateThread.ts';
import usePostThreadHistory from '../../api/usePostThreadHistory.ts';
import useSendMessage from '../../api/useSendMessage.ts';
import createSseLineProcessor from './sseLineProcessor.ts';
import { useEffectOnce } from '../../lib/useEffectOnce.ts';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import { toast } from 'react-toastify';
import useBotPagesConfig from '../../hooks/useBotPagesConfig.ts';
import useLocalStorage from '../../lib/useLocalStorage.ts';
import classNames from 'classnames';
import MarkdownViewer from '../MarkdownViewer';

const maxMessageLength = 500;
declare global {
    interface Window {
        downloadCapturedUpdates?: () => void;
        toggleCaptureUpdates?: () => void;
        captureUpdates?: boolean;
    }
}

interface KnowledgeAssistantProps {
    initialQuery?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveMessageType(item: ItemDto): ResponsesMessageType {
    if (item.type === 'message') {
        const msg = item as MessageItemDto;
        if (msg.role === 'user') return 'user_message';
        if (msg.role === 'system') return 'system_message';
        if (msg.role === 'developer') return 'developer_message';
        return 'assistant_message';
    }
    const typeMap: Partial<Record<string, ResponsesMessageType>> = {
        file_search_call: 'file_search_call',
        function_call: 'function_call',
        reasoning: 'reasoning',
        code_interpreter_call: 'code_interpreter_call',
        web_search_call: 'web_search_call',
        mcp_list_tools: 'mcp_list_tools',
        mcp_call: 'mcp_call',
        mcp_approval_request: 'mcp_approval_request',
        mcp_approval_response: 'mcp_approval_response'
    };
    return typeMap[item.type] ?? 'unknown_or_unsupported';
}

// ─── Component ────────────────────────────────────────────────────────────────

const KnowledgeAssistant: React.FC<KnowledgeAssistantProps> = ({
    initialQuery
}) => {
    const { title: configTitle } = useBotPagesConfig();
    const { threadId, setThreadId } = useContext(ThreadContext);
    const navigate = useNavigate();

    const [messages, setMessages] = useState<ResponsesMessageRecord[]>([]);
    const [inputText, setInputText] = useState<string>('');
    const [isAssistantTyping, setIsAssistantTyping] = useState<boolean>(false);
    const [conversationReady, setConversationReady] = useState<boolean>(false);
    const hasSentInitialQueryRef = useRef<boolean>(false);
    const [capturedUpdates, setCapturedUpdates] = useState<string[]>([]);
    const [citations, setCitations] = useState<CitationInfo[]>([]);

    const postThreadHistory = usePostThreadHistory();
    const postCreateThread = usePostCreateThread();
    const postThreadHistoryMutation = useMutation(postThreadHistory);
    const postCreateThreadMutation = useMutation(postCreateThread);

    const conversationHistoryRef = useRef<HTMLDivElement | null>(null);
    const isAtBottomRef = useRef(true);

    const handleConversationScroll = useCallback(() => {
        const el = conversationHistoryRef.current;
        if (!el) return;
        isAtBottomRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    }, []);

    useEffect(() => {
        const el = conversationHistoryRef.current;
        if (el && isAtBottomRef.current) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages, isAssistantTyping]);

    const [captureUpdates, setCaptureUpdates] = useLocalStorage(
        'captureUpdates',
        false
    );

    // ─── Streaming update handler ────────────────────────────────────────────

    const onStreamingUpdate = useCallback(
        (event: StreamingUpdateData) => {
            if (captureUpdates) {
                setCapturedUpdates((prev) => [
                    ...prev,
                    `[${new Date().toISOString()}] ${JSON.stringify(event, null, 2)}`
                ]);
            }

            switch (event.type) {
                case 'done':
                    setIsAssistantTyping(false);
                    break;

                case 'error':
                    setIsAssistantTyping(false);
                    toast.error(
                        'An error occurred, please try refreshing the page or asking the question again.'
                    );
                    break;

                case 'response.output_item.added': {
                    setIsAssistantTyping(true);
                    const item = event.item;
                    const itemId =
                        (item as { id?: string }).id ?? `local-${Date.now()}`;
                    const newRecord: ResponsesMessageRecord = {
                        id: itemId,
                        threadId: threadId ?? '',
                        messageType: deriveMessageType(item),
                        messageStatus: 'in_progress',
                        message: item,
                        metadata: {},
                        createdAt: new Date().toISOString()
                    };
                    setMessages((prev) =>
                        prev.some((r) => r.id === itemId)
                            ? prev
                            : [...prev, newRecord]
                    );
                    break;
                }

                case 'response.output_text.delta': {
                    setIsAssistantTyping(true);
                    const { item_id, delta } = event;
                    setMessages((prev) =>
                        prev.map((record) => {
                            if (record.id !== item_id) return record;
                            const msgItem =
                                record.message as MessageItemDto | null;
                            if (!msgItem || msgItem.type !== 'message')
                                return record;

                            const existing = msgItem.content ?? [];
                            const outIdx = existing.findIndex(
                                (p) => p.type === 'output_text'
                            );
                            const updated: ContentPartDto[] =
                                outIdx >= 0
                                    ? existing.map((p, i) =>
                                          i === outIdx
                                              ? {
                                                    ...(p as OutputTextContentPartDto),
                                                    text:
                                                        (
                                                            p as OutputTextContentPartDto
                                                        ).text + delta
                                                }
                                              : p
                                      )
                                    : [
                                          ...existing,
                                          {
                                              type: 'output_text',
                                              text: delta,
                                              annotations: []
                                          } as OutputTextContentPartDto
                                      ];

                            return {
                                ...record,
                                message: { ...msgItem, content: updated }
                            };
                        })
                    );
                    break;
                }

                case 'response.output_item.done': {
                    const { item, citationsData } = event;
                    const itemId = (item as { id?: string }).id;
                    if (itemId) {
                        setMessages((prev) =>
                            prev.map((record) =>
                                record.id === itemId
                                    ? {
                                          ...record,
                                          message: item,
                                          messageStatus: 'completed'
                                      }
                                    : record
                            )
                        );
                    }
                    if (citationsData && citationsData.length > 0) {
                        setCitations((prev) => {
                            const map = new Map(
                                prev.map((c) => [c.fileId, c])
                            );
                            citationsData.forEach((c) => {
                                const existing = map.get(c.fileId);
                                if (!existing || c.score > existing.score) {
                                    map.set(c.fileId, c);
                                }
                            });
                            return Array.from(map.values()).sort(
                                (a, b) => b.score - a.score
                            );
                        });
                    }
                    break;
                }

                default:
                    break;
            }
        },
        [threadId, captureUpdates]
    );

    // ─── SSE line buffering ───────────────────────────────────────────────────

    const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState<number>(
        Date.now()
    );

    const processSseLine = useMemo(
        () => createSseLineProcessor(onStreamingUpdate),
        [onStreamingUpdate]
    );

    const onLineReceived = useCallback(
        (line: string) => {
            setLastUpdateTimestamp(Date.now());
            processSseLine(line);
        },
        [processSseLine]
    );

    const sendMessageQuery = useSendMessage(onLineReceived);

    // ─── Timeout recovery ────────────────────────────────────────────────────

    const TIMEOUT_MS = 20_000;
    useEffect(() => {
        if (!isAssistantTyping) return;

        const interval = setInterval(() => {
            if (Date.now() - lastUpdateTimestamp > TIMEOUT_MS) {
                console.warn("No 'done' event received, attempting recovery...");
                setIsAssistantTyping(false);

                if (threadId) {
                    postThreadHistoryMutation.mutateAsync(
                        { threadId },
                        {
                            onSuccess: (response) => {
                                if (response) {
                                    setMessages(response.messages);
                                    setCitations(response.files);
                                }
                            },
                            onError: (error) => {
                                console.error(
                                    'Failed to refresh thread history:',
                                    error
                                );
                            }
                        }
                    );
                } else {
                    toast.error(
                        'Request timed out, please refresh and try again.'
                    );
                }

                clearInterval(interval);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [isAssistantTyping, lastUpdateTimestamp, postThreadHistoryMutation, threadId]);

    // ─── Initial thread history load ─────────────────────────────────────────

    useEffectOnce(() => {
        if (threadId) {
            postThreadHistoryMutation.mutateAsync(
                { threadId },
                {
                    onSuccess: (response) => {
                        if (response) {
                            setMessages(response.messages);
                            setCitations(response.files);
                        }
                    },
                    onError: (error: unknown) => {
                        toast.error(unknownErrorToString(error));
                    },
                    onSettled: () => {
                        setConversationReady(true);
                    }
                }
            );
        } else {
            setConversationReady(true);
        }
    });

    // ─── Send message ────────────────────────────────────────────────────────

    const sendMessage = useCallback(
        async (messageText: string) => {
            if (messageText.trim() === '') return;

            const userRecord: ResponsesMessageRecord = {
                id: `local-${Date.now()}`,
                threadId: threadId || '',
                messageType: 'user_message',
                messageStatus: 'in_progress',
                message: {
                    type: 'message',
                    role: 'user',
                    content: [{ type: 'input_text', text: messageText }]
                } as MessageItemDto,
                metadata: {},
                createdAt: new Date().toISOString()
            };

            setMessages((prev) => [...prev, userRecord]);
            setIsAssistantTyping(true);

            let existingOrNewThreadId = threadId;
            if (!existingOrNewThreadId) {
                const createThreadResponse =
                    await postCreateThreadMutation.mutateAsync(
                        {},
                        {
                            onError: (error) => {
                                toast.error(
                                    'Unable to create a new thread: ' +
                                        unknownErrorToString(error)
                                );
                            }
                        }
                    );

                if (createThreadResponse) {
                    existingOrNewThreadId = createThreadResponse.threadId;
                    setThreadId(existingOrNewThreadId);
                }
            }

            if (existingOrNewThreadId) {
                await sendMessageQuery.mutateAsync({
                    threadId: existingOrNewThreadId,
                    message: messageText
                });
            }
        },
        [threadId, postCreateThreadMutation, sendMessageQuery, setThreadId]
    );

    // ─── Initial query ───────────────────────────────────────────────────────

    useEffect(() => {
        if (
            conversationReady &&
            initialQuery &&
            !hasSentInitialQueryRef.current
        ) {
            sendMessage(initialQuery);
            hasSentInitialQueryRef.current = true;

            const params = new URLSearchParams(window.location.search);
            params.delete('searchText');
            navigate(
                {
                    pathname: window.location.pathname,
                    search: params.toString()
                },
                { replace: true }
            );
        }
    }, [conversationReady, initialQuery, sendMessage, navigate]);

    // ─── Debug capture ───────────────────────────────────────────────────────

    useEffect(() => {
        if (!captureUpdates) return;
        const downloadCapturedUpdates = () => {
            const text = capturedUpdates.join('\n');
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'captured_updates.txt';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        };
        window.downloadCapturedUpdates = downloadCapturedUpdates;
        return () => {
            delete window.downloadCapturedUpdates;
        };
    }, [capturedUpdates, captureUpdates]);

    const toggleCaptureUpdates = useCallback(() => {
        setCaptureUpdates((prev) => !prev);
    }, [setCaptureUpdates]);

    useEffect(() => {
        window.toggleCaptureUpdates = toggleCaptureUpdates;
        return () => {
            delete window.toggleCaptureUpdates;
        };
    }, [toggleCaptureUpdates]);

    useEffect(() => {
        window.captureUpdates = captureUpdates;
    }, [captureUpdates]);

    // ─── Send button ─────────────────────────────────────────────────────────

    const handleSendMessage = useCallback(() => {
        if (inputText.trim() === '') return;
        sendMessage(inputText);
        setInputText('');
    }, [inputText, sendMessage]);

    // ─── Citation sidebar helpers ─────────────────────────────────────────────

    const formFollowUpSeparator = '#↷';

    const isForm = (text: string): boolean => {
        if (!text) return false;
        if (text.indexOf('calculator=') !== -1) return true;
        const parts = text.split(formFollowUpSeparator);
        return parts.length === 2 && isForm(parts[1]);
    };

    const getQuestionFromFormFollowUp = (text: string) => {
        const parts = text.split(formFollowUpSeparator);
        return parts.length === 2 ? parts[0] : null;
    };

    const askQuestion = (question: string) => {
        if (window.faqbot) {
            window.faqbot.open();
            window.faqbot.askQuestion(question);
        }
    };

    const FollowUps = ({ citation }: { citation: CitationInfo }) =>
        citation.followUpQuestions.map((question, qIndex) => {
            const isFormCitation = isForm(question.text);
            return (
                <li key={qIndex}>
                    {isAssistantTyping ? (
                        <span>{question.text}</span>
                    ) : isFormCitation ? (
                        <>
                            <a
                                href='#'
                                onClick={(event) => (
                                    event.preventDefault(),
                                    askQuestion(question.text)
                                )}
                            >
                                {getQuestionFromFormFollowUp(question.text) ??
                                    question.text}{' '}
                            </a>
                            - 🤖
                        </>
                    ) : (
                        <a href={question.url}>{question.text}</a>
                    )}
                </li>
            );
        });

    const getIcon = (citationType: CitationType) => {
        switch (citationType) {
            case CitationType.Article:
                return FaFileAlt;
            case CitationType.Web:
                return FaLink;
            default:
                return FaQuestionCircle;
        }
    };

    const renderCitationHeaderAndIcon = (citation: CitationInfo) => {
        const CitationIcon = getIcon(citation.citationType);
        return (
            <>
                <div className='d-inline-flex'>
                    <CitationIcon
                        size={18}
                        className='me-2 text-secondary'
                        aria-label='Article'
                    />
                </div>
                <h5 className='d-inline-flex citation-title align-items-center'>
                    {citation.title}
                </h5>
            </>
        );
    };

    const nonWebCitations = citations
        .map((c, i) => ({ ...c, originalIndex: i }))
        .filter((c) => c.citationType !== CitationType.Web);

    const webCitations = citations
        .map((c, i) => ({ ...c, originalIndex: i }))
        .filter((c) => c.citationType === CitationType.Web);

    // Per-message [turn][file] file_search results, used to resolve OpenAI
    // private-use citation markers (fileciteturnNfileM) to file ids.
    const turnFileIdMap = useMemo(
        () => buildTurnFileIdMap(messages),
        [messages]
    );

    const handleCitationClick = useCallback((fileId: string) => {
        document
            .getElementById(`Citation_${fileId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <Container className='chat-page'>
            <Row className='chat-container'>
                <Col xs={12} lg={8}>
                    <div className='chat-box'>
                        <h4 className='citations-header'>
                            {configTitle || 'Help pages assistant'}
                        </h4>
                        <div
                            className='conversation-history'
                            ref={conversationHistoryRef}
                            onScroll={handleConversationScroll}
                        >
                            <ListGroup>
                                {messages.map((record, index) => (
                                    <ListGroupItem
                                        key={record.id || index}
                                        className={`d-flex ${
                                            record.messageType === 'user_message'
                                                ? 'justify-content-end'
                                                : 'justify-content-start'
                                        }`}
                                    >
                                        <MessageBubble
                                            record={record}
                                            citations={citations}
                                            onCitationClick={handleCitationClick}
                                            turns={turnFileIdMap[record.id]}
                                        />
                                    </ListGroupItem>
                                ))}
                                {isAssistantTyping && (
                                    <ListGroupItem className='d-flex justify-content-start'>
                                        <div className='message-bubble bg-light text-dark rounded'>
                                            <div className='typing-indicator'>
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                        </div>
                                    </ListGroupItem>
                                )}
                            </ListGroup>
                        </div>
                        <footer className='chat-input'>
                            <Input
                                className='chat-textarea'
                                type='textarea'
                                value={inputText}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= maxMessageLength) {
                                        setInputText(value);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === 'Enter' &&
                                        !e.shiftKey &&
                                        inputText.length <= maxMessageLength
                                    ) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                placeholder='Type your message...'
                                maxLength={maxMessageLength}
                            />
                            <Button
                                color='primary'
                                className='chat-button'
                                onClick={handleSendMessage}
                                disabled={
                                    isAssistantTyping ||
                                    inputText.length > maxMessageLength
                                }
                            >
                                <FaPaperPlane />
                            </Button>
                            <div className='d-flex justify-content-between align-items-center mt-1'>
                                <small
                                    className={classNames({
                                        'd-none':
                                            inputText.length < maxMessageLength,
                                        'text-danger':
                                            inputText.length >= maxMessageLength
                                    })}
                                >
                                    {inputText.length}/{maxMessageLength}
                                </small>
                                {inputText.length > maxMessageLength && (
                                    <small className='text-danger ms-2'>
                                        Message too long (max {maxMessageLength}{' '}
                                        characters)
                                    </small>
                                )}
                            </div>
                        </footer>
                    </div>
                </Col>
                <Col xs={12} lg={4}>
                    <div className='citations-sidebar'>
                        <div className='citations-container'>
                            <h4 className='citations-header'>
                                Supporting information
                            </h4>
                            <div className='citations-list'>
                                {nonWebCitations.map((citation, index) => (
                                    <div
                                        id={`Citation_${citation.fileId}`}
                                        key={index}
                                        className={`citation-block ${
                                            citation.citationType ===
                                            CitationType.Article
                                                ? 'citation-clickable'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            if (
                                                citation.citationType ===
                                                    CitationType.Article &&
                                                citation.url
                                            ) {
                                                window.open(
                                                    citation.url,
                                                    '_blank'
                                                );
                                            }
                                        }}
                                    >
                                        <div className='me-2'>
                                            <Badge
                                                color='primary'
                                                pill
                                                className='align-text-top'
                                                style={{
                                                    fontSize: '0.75rem',
                                                    padding: '0.25em 0.6em'
                                                }}
                                            >
                                                {citation.originalIndex + 1}
                                            </Badge>
                                        </div>
                                        <div className='flex-grow-1'>
                                            <div className='d-flex'>
                                                {renderCitationHeaderAndIcon(
                                                    citation
                                                )}
                                            </div>
                                            <div className='citation-content'>
                                                {isForm(citation.content) ? (
                                                    <div className='d-flex justify-content-end'>
                                                        <a
                                                            title={`Ask the chatbot: ${citation.title}`}
                                                            href='#'
                                                            className='btn btn-outline-primary rounded-pill m-1'
                                                            onClick={(event) => (
                                                                event.preventDefault(),
                                                                askQuestion(
                                                                    citation.title
                                                                )
                                                            )}
                                                        >
                                                            Click here
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <MarkdownViewer
                                                        content={citation.content}
                                                    />
                                                )}
                                            </div>
                                            {citation.citationType ===
                                                CitationType.QA &&
                                                citation.followUpQuestions
                                                    ?.length > 0 && (
                                                    <div className='follow-up-questions'>
                                                        <h6>
                                                            Follow-up Questions:
                                                        </h6>
                                                        <ul>
                                                            <FollowUps
                                                                citation={citation}
                                                            />
                                                        </ul>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                ))}
                                {webCitations.length > 0 && (
                                    <div
                                        id='Citation_urls'
                                        className='citation-block'
                                    >
                                        <div className='flex-grow-1'>
                                            <div className='d-flex'>
                                                <FaLink
                                                    size={18}
                                                    className='me-2 text-secondary'
                                                    aria-label='Article'
                                                />
                                                <h5>URLs found</h5>
                                            </div>
                                        </div>
                                        <div className='citation-content'>
                                            <ul>
                                                {webCitations.map(
                                                    (citation, index) => (
                                                        <li key={index}>
                                                            <a
                                                                id={`Citation_${citation.fileId}`}
                                                                href={
                                                                    citation.url
                                                                }
                                                            >
                                                                {citation.url}
                                                            </a>
                                                            <Badge
                                                                color='primary'
                                                                pill
                                                                className='align-text-top ms-2'
                                                                style={{
                                                                    fontSize:
                                                                        '0.75rem',
                                                                    padding:
                                                                        '0.25em 0.6em'
                                                                }}
                                                            >
                                                                {citation.originalIndex +
                                                                    1}
                                                            </Badge>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className='mt-1'>
                <Col>
                    <p className='opacity-50 text-center'>
                        <small>
                            AI can make mistakes. Check supporting information
                            to be sure.
                        </small>
                    </p>
                </Col>
            </Row>
        </Container>
    );
};

export default KnowledgeAssistant;
